import React from 'react';
import { Flex } from 'deprecated-enkel';
import styled from 'styled-components';
import { margin } from 'polished';

import { MEAL_CATEGORIES } from 'constants/categories-constants';
import { CardActions } from 'data/functions/card-actions';

import { checkIfProductIsAvailable, getGrams, getKgs } from 'utils';

import { TotemButton } from 'ui/Button';
import { ExtendedProductHeaderProps } from 'pages/Totem/TotemProducts/TotemProducts.types';
import {
    ProductFlow,
    useAddProductMutation,
    useLessProductMutation,
    useMoreProductMutation,
    useRemoveProductMutation,
} from 'data/__generated__';
import { MODIFIABLE_ORDER_STATES } from 'constants/states-app';

export const ExtendedProductInfoButtons: React.FC<ExtendedProductHeaderProps> = ({
    product,
    order,
    currentCategory,
}) => {
    // Check if the order is modifiable based on its state
    const isOrderModifiable = order?.state ? MODIFIABLE_ORDER_STATES.includes(order.state) : false;

    // Check if caterer products are locked (catererLocked flag + product is CATERER flow)
    const isCatererProductLocked = order?.catererLocked && product?.flow === ProductFlow.Caterer;

    // Product modification is disabled if order is not modifiable OR if caterer product is locked
    const isProductModificationDisabled = !isOrderModifiable || isCatererProductLocked;

    const [addProduct, addProductData] = useAddProductMutation();
    const [removeProduct, removeProductData] = useRemoveProductMutation();
    const [lessProduct, lessProductData] = useLessProductMutation();
    const [moreProduct, moreProductData] = useMoreProductMutation();

    const handlers = {
        addProduct: {
            handler: addProduct,
            data: addProductData,
        },
        removeProduct: {
            handler: removeProduct,
            data: removeProductData,
        },
        moreProduct: {
            handler: moreProduct,
            data: moreProductData,
        },
        lessProduct: {
            handler: lessProduct,
            data: lessProductData,
        },
    };

    const { handleAddProduct, handleRemoveProduct, handleMoreProduct, handleLessProduct } = CardActions(
        { product, order, currentCategory },
        handlers,
    );

    const productInOrder = order?.products.find(({ _id }) => _id === product?._id);
    let quantityToDisplay = null;

    if (productInOrder) {
        quantityToDisplay =
            typeof productInOrder.quantity === 'number' ? parseFloat(productInOrder.quantity.toFixed(3)) : 0;
    }

    const { conditionningTotem, displayKg, portion } = product ?? {};

    const quantityForecast = parseFloat(((conditionningTotem ?? 0) / (portion ?? 0)).toFixed(3));
    const isQtyMoreThanAvailableStock =
        (productInOrder?.quantity || 0) + quantityForecast > ((product as any).stockOnline ?? 0);
    const isNotEnoughStock =
        !product?.isWithoutStock &&
        !product?.allowNegativeStock &&
        !product?.canBeSuppliedBeforeOrderDate &&
        isQtyMoreThanAvailableStock;

    const orderProductIds = order?.products.map(({ _id }) => _id);
    const isAlreadyInOrder = orderProductIds?.includes(product?._id ?? '');
    const isProductAvailable = product && checkIfProductIsAvailable(product);

    return (
        <ProductAdjustQuantity>
            {productInOrder && (
                <Flex $direction="column" $justifyContent="flex-start" $alignItems="center">
                    <ProductPortions>
                        {displayKg
                            ? getKgs(
                                  product?.displayKg ?? false,
                                  product?.weightWithoutPackaging ?? 0,
                                  quantityToDisplay ?? 0,
                              )
                            : quantityToDisplay}{' '}
                        {displayKg ? 'kg ' : ''}
                        {displayKg
                            ? getGrams(
                                  product?.displayKg ?? false,
                                  product?.weightWithoutPackaging ?? 0,
                                  quantityToDisplay ?? 0,
                              )
                            : ''}
                        {displayKg
                            ? getGrams(
                                  product?.displayKg ?? false,
                                  product?.weightWithoutPackaging ?? 0,
                                  quantityToDisplay ?? 0,
                              )
                                ? 'g'
                                : ''
                            : 'pièce'}
                        {(quantityToDisplay ?? 0) > 1 && !displayKg ? 's' : ''}
                    </ProductPortions>
                    {/* @ts-ignore */}
                    {!MEAL_CATEGORIES.includes(product?.categoryId ?? '') && product?.volume !== 'Pièce' && (
                        <ProductPortions subtext>
                            Soit {productInOrder?.portion} portion{(productInOrder?.portion ?? 0) > 1 ? 's' : ''}
                        </ProductPortions>
                    )}
                </Flex>
            )}
            {isProductAvailable && (
                <>
                    {productInOrder?.quantity ? (
                        <Flex $direction="column">
                            <Flex>
                                <TotemButton
                                    disabled={isProductModificationDisabled}
                                    $type="secondary"
                                    data-test="extended-product-less-button"
                                    $fontSize="0.8em"
                                    onClick={handleLessProduct}
                                    $margins={[0, 0.15, 0.3, 0]}
                                >
                                    -
                                </TotemButton>
                                <TotemButton
                                    data-test="extended-product-more-button"
                                    disabled={isNotEnoughStock || isProductModificationDisabled}
                                    $fontSize="0.8em"
                                    onClick={handleMoreProduct}
                                    $margins={[0, 0, 0.3, 0.15]}
                                >
                                    +
                                </TotemButton>
                            </Flex>

                            <TotemButton
                                $fontSize="0.7em"
                                onClick={handleRemoveProduct}
                                disabled={isProductModificationDisabled}
                            >
                                Supprimer
                            </TotemButton>
                        </Flex>
                    ) : (
                        <Flex>
                            <TotemButton
                                disabled={isNotEnoughStock || isProductModificationDisabled}
                                $fontSize="1em"
                                onClick={handleAddProduct}
                            >
                                Ajouter
                            </TotemButton>
                        </Flex>
                    )}
                </>
            )}
            {!isProductAvailable && isAlreadyInOrder && (
                <TotemButton $fontSize="0.7em" onClick={handleRemoveProduct} disabled={isProductModificationDisabled}>
                    Supprimer
                </TotemButton>
            )}
        </ProductAdjustQuantity>
    );
};

type ExtendedProductInfoButtonsPropsCss = {
    alignItems?: string;
    $noMargin?: boolean;
    subtext?: boolean;
};
const ProductAdjustQuantity = styled(Flex.Style)<ExtendedProductInfoButtonsPropsCss>`
    justify-content: flex-end;
    align-items: ${(props) => (props.alignItems ? props.alignItems : 'flex-start')};
    flex-shrink: 0;
    color: ${(props) => props.theme.colors.black};
    border-radius: ${(props) => props.theme.variables.borderRadius};
    ${(props) => !props.$noMargin && margin(props.theme.variables.margin * 2, props.theme.variables.margin * 2, 0, 0)};
`;

const ProductPortions = styled.div<ExtendedProductInfoButtonsPropsCss>`
    align-self: flex-end;
    ${(props) => margin(0, props.theme.variables.margin, 0, 0)};
    font-size: ${(props) => (props.subtext ? '1em' : '1.5em')};
    text-transform: uppercase;
    letter-spacing: 0.2px;
    font-weight: ${(props) => (props.subtext ? 600 : 800)};
    color: ${(props) => (props.subtext ? props.theme.colors.black : props.theme.colors.black)};
`;
